import React, { useState, useRef, useCallback, useLayoutEffect } from "react";

import { createPortal } from "react-dom";

import * as S from "./OverflowTooltip.style";

interface Props {
  items: React.ReactNode[];
  maxVisible?: number;
  maxLines?: number;
}

export const OverFlowTooltip = ({ items, maxVisible = 4, maxLines }: Props) => {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [finalPos, setFinalPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const hasListOverflow = !maxLines && items.length > maxVisible;

  const handleMouseEnter = useCallback(() => {
    if (!containerRef.current) return;

    if (maxLines) {
      const el = containerRef.current;
      if (el.scrollHeight <= el.clientHeight) return;
    } else if (!hasListOverflow) {
      return;
    }

    setFinalPos(null);
    setAnchorRect(containerRef.current.getBoundingClientRect());
  }, [hasListOverflow, maxLines]);

  const handleMouseLeave = useCallback(() => {
    setAnchorRect(null);
    setFinalPos(null);
  }, []);

  useLayoutEffect(() => {
    if (!anchorRect || !tooltipRef.current) return;

    const tooltipHeight = tooltipRef.current.offsetHeight;
    const tooltipWidth = tooltipRef.current.offsetWidth;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const top =
      anchorRect.bottom + tooltipHeight > viewportHeight
        ? anchorRect.top - tooltipHeight
        : anchorRect.bottom;

    const left =
      anchorRect.left + tooltipWidth > viewportWidth
        ? viewportWidth - tooltipWidth - 8
        : anchorRect.left;

    setFinalPos({ top, left });
  }, [anchorRect]);

  const tooltipPortal = anchorRect
    ? createPortal(
        <S.TooltipWrapper
          ref={tooltipRef}
          style={{
            top: finalPos?.top ?? anchorRect.bottom,
            left: finalPos?.left ?? anchorRect.left,
            visibility: finalPos ? "visible" : "hidden",
          }}
        >
          {items}
        </S.TooltipWrapper>,
        document.body,
      )
    : null;

  if (maxLines) {
    return (
      <>
        <S.LineClampContainer
          ref={containerRef}
          maxLines={maxLines}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {items}
        </S.LineClampContainer>
        {tooltipPortal}
      </>
    );
  }

  if (!hasListOverflow) {
    return <S.OverflowContainer>{items}</S.OverflowContainer>;
  }

  return (
    <>
      <S.OverflowContainer
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {items.slice(0, maxVisible)}
        <S.EllipsisIndicator>...</S.EllipsisIndicator>
      </S.OverflowContainer>
      {tooltipPortal}
    </>
  );
};

export default OverFlowTooltip;
